import NumeralService from '/@/modules/core/app/services/NumeralService';
import { ColumnFormat } from '/@/modules/core/app/constants/data.constants';
import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import { EstateDistributionType } from '/@/modules/estatedistribution/enums/EstateDistributionType';
import { SpecificDistributionAllocationType } from '/@/modules/estatedistribution/enums/SpecificDistributionAllocationType';
import { ModelType } from '/@/modules/core/app/constants/ModelType';
import { EstateDistributionRecipientType } from '/@/modules/estatedistribution/enums/EstateDistributionRecipientType';
import { EstateDistributionSpecificType } from '@/modules/estatedistribution/enums/EstateDistributionSpecificType';
import { AlternateType, AlternateTypeKey } from '@/modules/estatedistribution/enums/AlternateType';
import { EstateAccount } from '@/modules/estateaccount/models/EstateAccount';
import { Asset } from '/@/modules/asset/models/Asset';
import { DistributionAlternate } from '/@/modules/estatedistribution/models/DistributionAlternate';
import { User } from '/@/modules/user/models/User';
import Lang from '@/modules/core/app/services/Lang';

export class EstateDistribution extends BaseEntityModel {
  id?: number;
  estateId?: number;
  estateAccountId?: number;
  estateAccount?: EstateAccount;
  recipientType?: string;
  distributionType?: string;
  specificType?: string;
  alternateType?: AlternateTypeKey | null;
  assetId?: number;
  asset?: Asset;
  sharePercentage?: number;
  plannedAmount?: number;
  actualAmount?: number;
  isOverridden?: boolean;
  isSplitEqually?: boolean;
  allocationType?: string;
  distributionAlternates?: Relationship<DistributionAlternate[]>;
  distributionAlternateUsers?: Relationship<User[]>;

  constructor(model: Partial<EstateDistribution> = {}) {
    super(model);

    this.id = model.id;
    this.estateId = model.estateId;
    this.estateAccountId = model.estateAccountId;
    this.recipientType = model.recipientType ?? EstateDistributionRecipientType.MAIN.key;
    this.distributionType = model.distributionType;
    this.specificType = model.specificType;
    this.alternateType = model.alternateType;
    this.assetId = model.assetId;
    this.sharePercentage = model.sharePercentage ?? 0;
    this.actualAmount = model.actualAmount ?? 0;
    this.plannedAmount = this.initialPlannedAmount(model.plannedAmount);
    this.isOverridden = model.isOverridden ?? false;
    this.isSplitEqually = model.isSplitEqually ?? false;
    this.allocationType = model.allocationType ?? this.identifyAllocationType();

    this.asset = Asset.mapModelOrNull(model.asset) || undefined;
    this.estateAccount = EstateAccount.mapModelOrNull(model.estateAccount) || undefined;
    this.distributionAlternates =
      DistributionAlternate.mapModelsOrNull(model.distributionAlternates) || undefined;
    this.distributionAlternateUsers = User.mapModels(model.distributionAlternateUsers);
  }

  getModelName(): string {
    return ModelType.ESTATE_DISTRIBUTION;
  }

  get isTypeSpecific(): boolean {
    return this.distributionType === EstateDistributionType.SPECIFIC.key;
  }

  get isTypeResidual(): boolean {
    return this.distributionType === EstateDistributionType.RESIDUAL.key;
  }

  get setOffAmount(): number {
    if (this.plannedAmount) {
      return this.plannedAmount - (this.actualAmount ?? 0);
    }

    return 0;
  }

  get actualAmountDisplay(): string {
    return NumeralService.formatValue(this.actualAmount, ColumnFormat.FORMAT_CURRENCY, 2);
  }

  get plannedAmountDisplay(): string {
    return NumeralService.formatValue(this.plannedAmount, ColumnFormat.FORMAT_CURRENCY, 2);
  }
  
  get precisePlannedAmountDisplay(): string {
    return NumeralService.formatValue(this.plannedAmount, ColumnFormat.FORMAT_CURRENCY, 2, false, '$', true);
  }

  get setOffAmountDisplay(): string {
    return NumeralService.formatValue(this.setOffAmount, ColumnFormat.FORMAT_CURRENCY, 2);
  }

  get isMainRecipientType(): boolean {
    return this.recipientType === EstateDistributionRecipientType.MAIN.key;
  }

  get isLumpSumPetSpecificGift(): boolean {
    return (
      this.isTypeSpecific &&
      this.isMainRecipientType &&
      this.specificType === EstateDistributionSpecificType.PET_LUMP_SUM.key
    );
  }

  get distributionTypeValue(): string | undefined {
    return this.distributionType
      ? EstateDistributionType.getByKey(this.distributionType)?.value
      : undefined;
  }

  get alternateTypeValue(): string | undefined {
    return AlternateType.getByKey(this.alternateType!)?.value;
  }

  initialPlannedAmount(plannedAmount: number | undefined): number | undefined {
    return plannedAmount! <= 0 && this.isTypeSpecific && this.actualAmount! > 0
      ? this.actualAmount
      : plannedAmount;
  }

  static formatSharePercentage(isSplitEqually: boolean, sharePercentage: number): string {
    if (isSplitEqually && sharePercentage) {
      return Lang.t('estatedistribution.fraction-allocation', {
        fraction: `1/${Math.round(100 / sharePercentage)}`
      });
    }

    return Lang.t('estatedistribution.percent-allocation', {
      percent: Math.round((sharePercentage ?? 0) * 100) / 100
    });
  }

  get sharePercentageDisplay(): string {
    return EstateDistribution.formatSharePercentage(
      this.isSplitEqually ?? false,
      this.sharePercentage ?? 0
    );
  }

  get isAssetDistribution(): boolean {
    return this.allocationType === SpecificDistributionAllocationType.ASSET.key;
  }

  get isAmountDistribution(): boolean {
    return this.allocationType === SpecificDistributionAllocationType.AMOUNT.key;
  }

  private identifyAllocationType(): string {
    if (this.isTypeResidual) {
      return '';
    }
    return this.assetId
      ? SpecificDistributionAllocationType.ASSET.key
      : SpecificDistributionAllocationType.AMOUNT.key;
  }
}
